import { GiftDeliveryZoneSidoSummary } from '@shared/api/giftDeliveryZone/giftDeliveryZone.interface';

/**
 * 배송권역 안내 검색어 → 공개 검색 파라미터.
 *
 * 공개 검색(q)은 공백을 지운 한 덩어리로 읍면동/시군구를 부분일치해서 "대구 동구"를 넣으면
 * "대구동구"가 돼 아무것도 안 걸린다. 그래서 앞쪽 단어를 시도·시군구로 떼어
 * 서버가 이미 받는 sido·sigungu 필터로 보낸다 (백엔드 무수정).
 *
 *  - "대구 동구"          → sido=대구, sigungu=동구
 *  - "대구광역시 동구 신암동" → sido=대구, sigungu=동구, q=신암동
 *  - "경북 포항시 북구"     → sido=경북, sigungu=포항시 북구 (여러 단어 시군구는 가장 긴 일치)
 *  - "동구 신암동"         → sigungu=동구, q=신암동
 *  - "동구" / "대구"       → 한 단어는 기존대로 q 부분일치 (광주=광역시·경기 광주시 둘 다 걸려야 함)
 */

// 백엔드 normalizeSido 와 같은 규칙 (gift-delivery-zone.normalize.ts)
const SIDO_SUFFIX = /(특별자치시|특별자치도|광역시|특별시|도)$/;
const SIDO_ALIASES: Record<string, string> = {
  경기도: '경기',
  충청북도: '충북',
  충청남도: '충남',
  전라북도: '전북',
  전라남도: '전남',
  경상북도: '경북',
  경상남도: '경남',
};
// 요약이 아직 안 왔을 때도 시도는 뗄 수 있게
const DEFAULT_SIDOS = [
  '서울',
  '부산',
  '대구',
  '인천',
  '광주',
  '대전',
  '울산',
  '세종',
  '경기',
  '강원',
  '충북',
  '충남',
  '전북',
  '전남',
  '경북',
  '경남',
  '제주',
];

export const normalizeZoneSido = (raw: string): string => {
  const s = raw.trim();
  if (!s) return '';
  return SIDO_ALIASES[s] ?? s.replace(SIDO_SUFFIX, '');
};

export interface GiftDeliveryZoneQuery {
  q?: string;
  sido?: string;
  sigungu?: string;
}

export const parseGiftDeliveryZoneQuery = (
  input: string,
  sidos?: GiftDeliveryZoneSidoSummary[] | null,
): GiftDeliveryZoneQuery => {
  const tokens = input.trim().split(/\s+/).filter(Boolean);
  const knownSidos = new Set([...DEFAULT_SIDOS, ...(sidos ?? []).map((s) => s.sido)]);
  if (tokens.length <= 1) {
    const only = tokens[0] ?? '';
    // "대구광역시"처럼 정식 명칭 한 단어만 치면 시도 전체로 (그냥 "대구"는 q로 둔다)
    const normalized = normalizeZoneSido(only);
    if (normalized !== only && knownSidos.has(normalized)) return { sido: normalized };
    return { q: only };
  }

  let rest = tokens;
  let sido: string | undefined;
  const firstSido = normalizeZoneSido(tokens[0] ?? '');
  if (knownSidos.has(firstSido)) {
    sido = firstSido;
    rest = tokens.slice(1);
  }

  // 시군구는 요약에 있는 이름과 정확히 같을 때만 필터로 (서버가 정확일치라 추측하면 0건)
  let sigungu: string | undefined;
  const sigunguNames = new Set(
    (sidos ?? [])
      .filter((s) => !sido || s.sido === sido)
      .flatMap((s) => s.sigungus.map((sg) => sg.sigungu))
      .filter(Boolean),
  );
  for (let k = rest.length; k >= 1; k -= 1) {
    const candidate = rest.slice(0, k).join(' ');
    if (sigunguNames.has(candidate)) {
      sigungu = candidate;
      rest = rest.slice(k);
      break;
    }
  }

  const q = rest.join(' ');
  return {
    ...(q ? { q } : {}),
    ...(sido ? { sido } : {}),
    ...(sigungu ? { sigungu } : {}),
  };
};
